import {
  type Dispatch,
  type SetStateAction,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  CustomPaging,
  IntegratedSelection,
  PagingState,
  SearchState,
  SelectionState,
  type Column,
  EditingState,
  SortingState,
  RowDetailState,
} from '@devexpress/dx-react-grid';
import {
  SearchPanel,
  TableEditRow,
  TableFixedColumns,
  TableColumnResizing,
  TableRowDetail,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import {debounce} from '@mui/material/utils';

import {
  type OptixColumnExtentions,
  type OptixTableProps,
} from './OptixTable.types';
import {OptixTableContext} from './OptixTableContext';
import {OptixTableBase} from './tableBase/OptixTableBase';
import {TableFixedColumnsCell} from './tableBase/TableFixedColumnsCell';
import OptixTableDataTypeAutocompleteProvider from './tableDataTypeProviders/optixTableDataTypeAutocompleteProvider/OptixTableDataTypeAutocompleteProvider';
import OptixTableDataTypeActionsProvider from './tableDataTypeProviders/tableDataTypeActionsProvider/OptixTableDataTypeActionsProvider';
import OptixTableDataTypeBooleanProvider from './tableDataTypeProviders/tableDataTypeBooleanProvider/OptixTableDataTypeBooleanProvider';
import OptixTableDataTypeDateProvider from './tableDataTypeProviders/tableDataTypeDateProvider/OptixTableDataTypeDateProvider';
import OptixTableDataTypeDateTimeProvider from './tableDataTypeProviders/tableDataTypeDateTimeProvider/OptixTableDataTypeDateTimeProvider';
import OptixTableDataTypeNumberProvider from './tableDataTypeProviders/tableDataTypeNumberProvider/OptixTableDataTypeNumberProvider';
import OptixTableDataTypeSelectProvider from './tableDataTypeProviders/tableDataTypeSelectProvider/OptixTableDataTypeSelectProvider';
import OptixTableDataTypeStringProvider from './tableDataTypeProviders/tableDataTypeStringProvider/OptixTableDataTypeStringProvider';
import OptixTableDataTypeTimeProvider from './tableDataTypeProviders/tableDataTypeTimeProvider/OptixTableDataTypeTimeProvider';
import OptixTablePaging from './tableFooter/OptixTablePaging';
import {OptixTableGrid} from './tableGrid/OptixTableGrid';
import {OptixTableHeaderRow} from './tableHeaderRow';
import OptixTableToolbar from './tableToolbar';
import OptixTableToolbarCustom from './tableToolbar/OptixTableToolbarCustom';
import OptixTableToolbarLoader from './tableToolbar/OptixTableToolbarLoader';
import OptixTableToolbarRefresh from './tableToolbar/OptixTableToolbarRefresh';
import OptixTableToolbarSearch from './tableToolbar/tableToolbarSearch/OptixTableToolbarSearch';
import {DEBOUNCE_DELAY} from '../../../modules/common/constants/DEBOUNCE_DELAY';
import {DEFAULT_TABLE_PAGE_SIZE} from '../../../modules/common/constants/DEFAULT_PAGE_SIZE';
import {useIsMobile} from '../../../modules/common/hooks/useIsMobile';

const TableWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isMobile' && prop !== 'isExpanded' && prop !== 'selectable',
})<{isMobile: boolean; selectable?: boolean}>(({
  theme,
  isMobile,
  selectable,
}) => {
  return {
    borderRadius: '1rem',
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,

    boxShadow: '0px 4px 10px rgba(233, 227, 227, 0.2)',
    border: '1px solid rgba(233, 227, 227, 0.15)',

    '& .dx-root .dx-datagrid-rowsview table': {
      minWidth: isMobile ? 600 : 800,
    },

    [theme.breakpoints.down('sm')]: {
      '& .dx-root .dx-datagrid-rowsview table': {
        minWidth: 500,
      },
    },

    ...(selectable
      ? {
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[4],
          '& .MuiTableBody-root .MuiTableRow-root': {
            cursor: 'pointer',
          },
          '& .MuiTableBody-root .MuiTableRow-root:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }
      : {}),
  };
});

export function OptixTable<TData = object>(
  props: OptixTableProps<TData>,
): JSX.Element {
  const {
    // Required
    config,
    list,
    // States
    loading,
    loadingText,
    page = 0,
    pageSize = DEFAULT_TABLE_PAGE_SIZE,
    rowId,
    total = 0,
    sorting,
    // Components
    CustomActions,
    RowDetails,
    rowActions = [], // Default To No Additional Row Actions
    // Handlers
    onDialogAdd,
    onInlineAdd,
    onDialogEdit,
    onInlineEdit,
    onDelete,
    onPageChange,
    onPageSizeChange,
    onSearch,
    onSortingChange,
    onRefresh,
    // Props
    PagingPanelProps,
    PagingStateProps,
    SearchPanelProps,
    SearchStateProps,
    TableHeaderRowProps,
    TableProps,
    ToolbarProps,
    // For Additional Buttons on Table
    enableRowActions = true,
    rowActionButtonSize = 'small',
    rowActionDisplayMode = 'icon-button',
    // Row selection
    enableRowSelection = false,
    selectionMode = 'multi',
    selection,
    onSelectionChange,
    selectByRowClick = true,
    showSelectAll = true,
    highlightSelectedRow = true,
    // Whether to use mobile wrapper or not
    ignoreWrapper,
  } = props;

  const isMobile = useIsMobile();

  const handleSelectionChange = useCallback(
    (newSelection: Array<string | number>) => {
      if (onSelectionChange == null) return;
      if (selectionMode === 'single') {
        const added = newSelection.filter(
          (id) => !(selection ?? []).includes(id),
        );
        onSelectionChange(added.length > 0 ? [added[added.length - 1]] : []);
        return;
      }
      onSelectionChange(newSelection);
    },
    [onSelectionChange, selection, selectionMode],
  );

  const [editingRows, setEditingRows] = useState<TData[]>([]);
  const [addedRows, setAddedRows] = useState<TData[]>([]);
  const [rowChanges, setRowChanges] = useState<Record<string, unknown>>({});
  const [expandedRowIds, setExpandedRowIds] = useState<Array<string | number>>(
    [],
  );

  const {
    enableAdd,
    enableEdit,
    enableDelete,
    enableActions,
    enableRefresh,
    enableRowActionsComputed,
    columns,
    columnExt,
    columnWidths,
    sortableColumns,
    booleanColumns,
    stringColumns,
    numberColumns,
    dateColumns,
    dateTimeColumns,
    timeColumns,
    selectColumns,
    autoCompleteColumns,
  } = useMemo(() => {
    const scopedEnableAdd = onDialogAdd != null || onInlineAdd != null;
    const scopedEnableEdit = onDialogEdit != null || onInlineEdit != null;
    const scopedEnableDelete = onDelete != null;
    const scopedEnableRefresh = onRefresh != null;

    const scopedEnableRowActions =
      enableRowActions && rowActions != null && rowActions.length > 0;

    const scopedEnableActions =
      scopedEnableAdd ||
      scopedEnableEdit ||
      scopedEnableDelete ||
      scopedEnableRowActions;

    const configColumns: Column[] = config.columns.map(
      ({name, label, getValue, enableEditing}) => ({
        name: String(name),
        title: label,
        getCellValue: getValue,
        editingEnabled: enableEditing,
      }),
    );

    const columnExtensions: OptixColumnExtentions[] = config.columns.map(
      ({name, enableEditing}) => ({
        columnName: String(name),
        editingEnabled: enableEditing,
      }),
    );

    if (scopedEnableActions) {
      const actionsColumn: Column = {
        name: 'actions',
        getCellValue: undefined,
        title: 'Actions' as string | undefined,
      };
      configColumns.push(actionsColumn);
    }

    let numOfInlineActions = 0;
    let numOfTableActions = 0;

    if (scopedEnableAdd) {
      numOfTableActions += 1;
    }
    if (scopedEnableEdit) {
      numOfInlineActions += 1;
    }
    if (scopedEnableDelete) {
      numOfInlineActions += 1;
    }

    if (scopedEnableRowActions) {
      if (
        rowActionDisplayMode === 'menu' ||
        rowActionDisplayMode === 'dropdown'
      ) {
        numOfInlineActions += 1;
      } else {
        numOfInlineActions += rowActions.length;
      }
    }

    const amountOfActions =
      numOfInlineActions > numOfTableActions
        ? numOfInlineActions
        : numOfTableActions;

    let calculatedActionsWidth;
    if (
      rowActionDisplayMode === 'menu' ||
      rowActionDisplayMode === 'dropdown'
    ) {
      calculatedActionsWidth = Math.max(120, 45 + amountOfActions * 45);
    } else {
      calculatedActionsWidth = Math.max(120, 45 + amountOfActions * 60);
    }

    return {
      enableAdd: scopedEnableAdd,
      enableEdit: scopedEnableEdit,
      enableDelete: scopedEnableDelete,
      enableActions: scopedEnableActions,
      enableRefresh: scopedEnableRefresh,
      enableRowActionsComputed: scopedEnableRowActions,
      actionsWidth: calculatedActionsWidth,
      columnExt: columnExtensions,
      columns: configColumns,
      columnWidths: configColumns.map((c) => ({
        columnName: c.name,
        width:
          c.name === 'actions'
            ? calculatedActionsWidth
            : (config.columns.find((col) => String(col.name) === c.name)
                ?.width ?? 'auto'),
      })),
      sortableColumns: [
        ...config.columns.map(({name, enableSort}) => ({
          columnName: String(name),
          sortingEnabled: enableSort !== false,
        })),
        {columnName: 'actions', sortingEnabled: false},
      ],
      booleanColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'boolean')
        .map(({name}) => String(name)),
      stringColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'string')
        .map(({name}) => String(name)),
      numberColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'number')
        .map(({name}) => String(name)),
      dateColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'date')
        .map(({name}) => String(name)),
      dateTimeColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'datetime')
        .map(({name}) => String(name)),
      timeColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'time')
        .map(({name}) => String(name)),
      autoCompleteColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'autocomplete')
        .map(({name}) => String(name)),
      selectColumns: config.columns
        .filter((column) => 'type' in column && column.type === 'select')
        .map(({name}) => String(name)),
    };
  }, [
    onDialogAdd,
    onInlineAdd,
    onDialogEdit,
    onInlineEdit,
    onDelete,
    onRefresh,
    rowActions,
    enableRowActions,
    rowActionDisplayMode,
    config.columns,
  ]);

  const enableToolbarLoading = loading !== undefined;
  const enableToolbarSearch = onSearch != null;
  const enableToolbar = true;
  const enablePaging = onPageChange != null;
  const enableSorting = onSortingChange != null && sorting != null;

  const tableJSX = (
    <OptixTableContext.Provider value={props as OptixTableProps<unknown>}>
      <OptixTableGrid columns={columns} rows={list}>
        {enableActions && (
          <OptixTableDataTypeActionsProvider
            enableAdd={enableAdd}
            enableEdit={enableEdit}
            enableDelete={enableDelete}
            enableRowActions={enableRowActionsComputed}
            rowActions={rowActions}
            rowActionDisplayMode={rowActionDisplayMode}
            rowActionButtonSize={rowActionButtonSize}
            addedRows={addedRows}
            rowChanges={rowChanges}
            setAddedRows={setAddedRows as Dispatch<SetStateAction<unknown>>}
            editingRows={editingRows}
            setEditingRows={setEditingRows as Dispatch<SetStateAction<unknown>>}
            expandedRowsId={expandedRowIds}
            setExpandedRowsId={setExpandedRowIds}
          />
        )}
        {booleanColumns.length > 0 && (
          <OptixTableDataTypeBooleanProvider for={booleanColumns} />
        )}
        {stringColumns.length > 0 && (
          <OptixTableDataTypeStringProvider for={stringColumns} />
        )}
        {numberColumns.length > 0 && (
          <OptixTableDataTypeNumberProvider for={numberColumns} />
        )}
        {dateColumns.length > 0 && (
          <OptixTableDataTypeDateProvider for={dateColumns} />
        )}
        {dateTimeColumns.length > 0 && (
          <OptixTableDataTypeDateTimeProvider for={dateTimeColumns} />
        )}
        {timeColumns.length > 0 && (
          <OptixTableDataTypeTimeProvider for={timeColumns} />
        )}
        {selectColumns.length > 0 && (
          <OptixTableDataTypeSelectProvider for={selectColumns} />
        )}
        {autoCompleteColumns.length > 0 && (
          <OptixTableDataTypeAutocompleteProvider for={autoCompleteColumns} />
        )}
        <EditingState
          editingRowIds={editingRows
            .filter(
              (row) =>
                row != null &&
                typeof row === 'object' &&
                rowId in row &&
                (row as Record<string, unknown>)[rowId] != null,
            )
            .map(
              (row) =>
                (row as Record<string, unknown>)[rowId] as string | number,
            )}
          rowChanges={rowChanges}
          onRowChangesChange={setRowChanges}
          addedRows={addedRows}
          columnExtensions={columnExt}
          onCommitChanges={() => {}}
        />
        {enableToolbarSearch && (
          <SearchState
            onValueChange={debounce(onSearch, DEBOUNCE_DELAY)}
            {...SearchStateProps}
          />
        )}
        {enableSorting && (
          <SortingState
            sorting={sorting}
            onSortingChange={onSortingChange}
            columnExtensions={sortableColumns}
          />
        )}
        {enablePaging && (
          <PagingState
            currentPage={page}
            onCurrentPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSize={pageSize}
            {...PagingStateProps}
          />
        )}
        {RowDetails && (
          <RowDetailState
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={setExpandedRowIds}
          />
        )}
        {enableRowSelection && (
          <SelectionState
            selection={selection}
            onSelectionChange={handleSelectionChange}
          />
        )}
        {enableRowSelection && <IntegratedSelection />}
        {enablePaging && <CustomPaging totalCount={total} />}
        <OptixTableBase
          colSpan={columns.length}
          loading={loading}
          loadingText={loadingText}
          {...TableProps}
        />
        {enableRowSelection && (
          <TableSelection
            selectByRowClick={selectByRowClick}
            highlightRow={highlightSelectedRow}
            showSelectAll={selectionMode === 'multi' && showSelectAll}
            showSelectionColumn={selectionMode === 'multi'}
          />
        )}
        <TableColumnResizing
          defaultColumnWidths={columnWidths}
          resizingMode="nextColumn"
          columnExtensions={[
            {
              columnName: 'actions',
            },
          ]}
        />
        <OptixTableHeaderRow
          {...TableHeaderRowProps}
          showSortingControls={enableSorting}
        />
        {RowDetails && (
          <TableRowDetail
            contentComponent={RowDetails}
            cellComponent={OptixTableRowDetailCellComponent}
          />
        )}
        {!config.disableToolbar && <OptixTableToolbar {...ToolbarProps} />}
        {enableRefresh ? <OptixTableToolbarRefresh /> : null}
        {!config.disableToolbar && enableToolbar && enableToolbarLoading && (
          <OptixTableToolbarLoader loading={loading} />
        )}
        {CustomActions != null && (
          <OptixTableToolbarCustom CustomActions={CustomActions} />
        )}
        <TableEditRow />
        {enableActions && (
          <TableFixedColumns
            cellComponent={TableFixedColumnsCell}
            rightColumns={['actions']}
          />
        )}
        {enableToolbarSearch && (
          <SearchPanel
            inputComponent={OptixTableToolbarSearch}
            {...SearchPanelProps}
          />
        )}
        {enablePaging && <OptixTablePaging {...PagingPanelProps} />}
      </OptixTableGrid>
    </OptixTableContext.Provider>
  );

  return (
    <Suspense fallback={null}>
      {ignoreWrapper ? (
        tableJSX
      ) : (
        <TableWrapper isMobile={isMobile} selectable={enableRowSelection}>
          {tableJSX}
        </TableWrapper>
      )}
    </Suspense>
  );
}

const OptixTableRowDetailCellComponent = styled(TableRowDetail.Cell)({
  padding: 0,
  borderBottomWidth: 0,
});
